import React, { useEffect } from 'react';
import { useResourceContext } from 'react-admin';
import { Alert } from '@medely/ui-kit/web';
import { ChargeRateCentsInput } from './rate-inputs/ChargeRateCentsInput';
import { RateStructureSelect } from './rate-inputs/RateStructureSelect';
import { BlendedRateCentsInput } from './rate-inputs/BlendedRateCentsInput';
import { BlendedRateMarkupInput } from './rate-inputs/BlendedRateMarkupInput';
import { MarginPercentInput } from './rate-inputs/MarginPercentInput';
import { RateSettingsDisplayHelper } from 'utils/displayHelpers/rate-settings';
import {
  calculateNewTotalMarginPercentW2,
  getClassificationFromSource,
  getSourceAndLabel,
} from './PositionRateSettingsForm.utils';
import { ProfessionalRateInput } from './rate-inputs/ProfessionalRateInput';
import { BaseMarginPercentW2LocalInput } from './rate-inputs/BaseMarginPercentW2LocalInput';
import { useFormContext } from 'react-hook-form';
import type { InheritedSettingsT } from '../types/inheritedSettings.types';

type Props = {
  source: string;
  inheritedSettings: InheritedSettingsT;
};

const LocationAndProfessionalRateInput = ({ source, inheritedSettings = {} }: Props) => {
  const { watch, setValue } = useFormContext();

  const charge_rate_cents = watch(`${source}.charge_rate_cents`);
  const markup = watch(`${source}.blended_rates.blended_rate_markup`);
  const margin = watch(getSourceAndLabel(source).source);

  const totalMarginPercentW2 = watch(`${source}.total_margin_percent_w2`);

  const resource = useResourceContext();
  const isW2Local = source.includes('w2.local');
  const isW2Travel = source.includes('w2.travel');
  const { tab } = getClassificationFromSource(source);

  const rateSettingsDisplayHelper = new RateSettingsDisplayHelper({ settings: inheritedSettings });
  const inheritedSettingsHash = rateSettingsDisplayHelper.getSettingsForClassification(tab);

  const isLocationLevelPositionRateSetting =
    resource === 'LocationPositionRateSetting' || resource === 'AgencyLocationPositionRateSetting';
  const show12hRatesFields = !isLocationLevelPositionRateSetting;
  const showRateStructureSelect =
    (['MarketPositionRateSetting'].includes(resource) || isLocationLevelPositionRateSetting) &&
    !isW2Travel;

  const calculateInitialRates = () => {
    let effectiveChargeRate = charge_rate_cents;
    let blendedRateValue;
    const isBlended = inheritedSettingsHash?.use_blended_rate;

    if (!charge_rate_cents) {
      effectiveChargeRate = inheritedSettingsHash.charge_rate_cents;
    }

    if (markup || isBlended) {
      blendedRateValue = effectiveChargeRate * (1 + markup);
    }

    if (isBlended) {
      effectiveChargeRate = blendedRateValue;
    }

    const inheritedMargin = Number(inheritedSettingsHash[getSourceAndLabel(source).field]);

    const effectiveMargin = margin || inheritedMargin;

    const professionalRate =
      effectiveChargeRate && effectiveMargin ? effectiveChargeRate * (1 - effectiveMargin) : null;

    return {
      blendedRateValue,
      effectiveChargeRate,
      effectiveMargin,
      professionalRate,
    };
  };

  useEffect(() => {
    setTimeout(() => {
      const { blendedRateValue, effectiveMargin, professionalRate } = calculateInitialRates();

      if (charge_rate_cents && blendedRateValue) {
        setValue(`${source}.blended_rate_cents`, blendedRateValue);
      }

      setValue(`${source}.pro_rate_cents`, professionalRate);

      if (!inheritedSettingsHash?.total_margin_percent_w2) {
        calculateNewTotalMarginPercentW2({
          baseW2Margin: effectiveMargin,
          setValue,
          source,
          watch,
        });
      } else {
        setValue(`${source}.total_margin_percent_w2`, totalMarginPercentW2);
      }
    }, 0);
  }, []);

  return (
    <>
      {showRateStructureSelect && (
        <RateStructureSelect source={source} inheritedSettings={inheritedSettingsHash} />
      )}

      <ProfessionalRateInput source={source} inheritedSettings={inheritedSettingsHash} />

      <ChargeRateCentsInput
        source={source}
        show12hRatesFields={show12hRatesFields}
        inheritedSettings={inheritedSettingsHash}
      />

      {show12hRatesFields && (
        <>
          <BlendedRateCentsInput source={source} inheritedSettings={inheritedSettingsHash} />
          <Alert
            title="12 hour rate structure"
            description="Please set the max hour fields appropriately based on the OT/XR policy that needs to be applied to the location and position."
            variant="filled"
            hideCloseIcon
          />

          <BlendedRateMarkupInput source={source} />
        </>
      )}

      {isW2Local && (
        <BaseMarginPercentW2LocalInput source={source} inheritedSettings={inheritedSettingsHash} />
      )}

      <MarginPercentInput source={source} inheritedSettings={inheritedSettings} />
    </>
  );
};

export default LocationAndProfessionalRateInput;
